import {
  GraphQLString,
  GraphQLNonNull,
  GraphQLObjectType,
  GraphQLID,
  GraphQLBoolean,
  GraphQLList,
  GraphQLInt
} from 'graphql'
import { gradeType, Grade } from './grade'
import { dateTimeType, DateTime } from './date-time-type'
import { GQLBugTracker, bugTrackerType } from './bug-trackers'
import { VulnerabilitySeverity } from '../shared'
import {
  ApplicationLibraryStatus,
  applicationLibraryStatusType
} from '../mutations/update-application-library-status'

interface LibraryUsage {
  readonly total: number
  readonly totalUsed: number
}

interface LibraryVersions {
  readonly current: {
    readonly version: string
    readonly releaseDate: DateTime
  }
  readonly latest: {
    readonly version: string
    readonly releaseDate: DateTime
  }
}

export interface LibraryPolicies {
  readonly restrictedByOrg: boolean
  readonly invalidVersion: boolean
  readonly versionRestriction: number | null
  readonly restrictedLicense?: boolean
}

export interface LibraryApplication {
  readonly id: string
  readonly appId: string
  readonly name: string
  readonly appLibraryStatus?: ApplicationLibraryStatus
}

export interface LibraryMergedApplication {
  readonly id: string
  readonly applicationID: string
  readonly name: string
  readonly appLibraryStatus?: ApplicationLibraryStatus
}

export type ConfidentialityImpact = 'None' | 'Partial' | 'Complete'
export type IntegrityImpact = 'None' | 'Partial' | 'Complete'
export type AvailabilityImpact = 'None' | 'Partial' | 'Complete'
export type AuthenticationRequired = 'None' | 'Single' | 'Multiple'
export type AccessVector = 'Local' | 'Adjacent Network' | 'Network'
export type AccessComplexity = 'Low' | 'Medium' | 'High'

interface VulnerabilityReference {
  readonly href: string
}

interface LibraryClassUsageCount {
  readonly appId: string
  readonly usageCount: number
}

export interface Vulnerability {
  readonly name: string
  readonly description: string
  readonly authentication: AuthenticationRequired
  readonly references: VulnerabilityReference[]
  readonly visible: boolean
  readonly accessVector?: AccessVector
  readonly accessComplexity?: AccessComplexity
  readonly availabilityImpact?: AvailabilityImpact
  readonly confidentialityImpact?: ConfidentialityImpact
  readonly integrityImpact?: IntegrityImpact
  readonly severityValue?: number
  readonly severityCode?: VulnerabilitySeverity
  readonly hasCve: boolean
  readonly hasCvss3: boolean
  readonly cvss3Vector?: string
  readonly cvss3SeverityValue?: number
  readonly cvss3SeverityCode?: VulnerabilitySeverity
}

export interface Library {
  readonly id: string
  readonly hash: string
  readonly group?: string
  readonly language: string
  readonly name: string
  readonly grade: Grade
  readonly policies: LibraryPolicies
  readonly ossEnabled: boolean
  readonly versions: LibraryVersions
  readonly licenses: string[]
  readonly tags: string[]
  readonly bugTrackers: GQLBugTracker[]
  readonly usage: LibraryUsage
  readonly applications: LibraryApplication[]
  readonly mergedApplications: LibraryMergedApplication[]
  readonly vulnerabilities: Vulnerability[]
  readonly libraryClassUsageCounts: LibraryClassUsageCount[]
}

const libraryUsageType = new GraphQLObjectType({
  name: 'LibraryUsage',
  fields: () => ({
    totalUsed: {
      description: 'The total number of library classes or files used',
      type: new GraphQLNonNull(GraphQLInt)
    },
    total: {
      description: 'The total number of library classes',
      type: new GraphQLNonNull(GraphQLInt)
    }
  })
})

const libraryPolicies = new GraphQLObjectType({
  name: 'LibraryPolicies',
  fields: () => ({
    restrictedByOrg: {
      description: 'Indicates if this library is restricted by an org',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    invalidVersion: {
      description:
        'This library is in violation of a policy that restricts it to be within a range of versions',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    versionRestriction: {
      description: 'This range of versions this library is outside of',
      type: GraphQLInt
    },
    restrictedLicense: {
      description:
        'Whether or not this Library contains a license restricted by the organization',
      type: GraphQLBoolean
    }
  })
})

const libraryVersionInfo = new GraphQLObjectType({
  name: 'LibraryVersionInfo',
  fields: () => ({
    version: {
      description: 'Library Version',
      type: new GraphQLNonNull(GraphQLString)
    },
    releaseDate: {
      description: 'Library Version Release Date',
      type: dateTimeType
    }
  })
})

const libraryVersionsInfo = new GraphQLObjectType({
  name: 'LibraryVersionsInfo',
  fields: () => ({
    current: {
      description: 'Current Version Info',
      type: new GraphQLNonNull(libraryVersionInfo)
    },
    latest: {
      description: 'Latest Version Info',
      type: new GraphQLNonNull(libraryVersionInfo)
    }
  })
})

const libraryApplication = new GraphQLObjectType({
  name: 'LibraryApplication',
  fields: () => ({
    id: {
      description:
        'The unique ID for the association between an application and a library',
      type: new GraphQLNonNull(GraphQLID)
    },
    appId: {
      description: 'The unique ID for an application',
      type: new GraphQLNonNull(GraphQLID)
    },
    name: {
      description: 'Human readable name for the application',
      type: new GraphQLNonNull(GraphQLString)
    },
    appLibraryStatus: {
      description: 'The status of the library related to this application',
      type: applicationLibraryStatusType
    }
  })
})

const libraryMergedApplication = new GraphQLObjectType({
  name: 'LibraryMergedApplication',
  fields: () => ({
    id: {
      description:
        'The unique ID for the made up of an application id and a library hash',
      type: new GraphQLNonNull(GraphQLID)
    },
    appId: {
      description: 'The unique ID for an application',
      type: new GraphQLNonNull(GraphQLID)
    },
    name: {
      description: 'Human readable name for the application',
      type: new GraphQLNonNull(GraphQLString)
    },
    appLibraryStatus: {
      description: 'The status of the library related to this application',
      type: GraphQLString
    }
  })
})

const vulnerabilityReference = new GraphQLObjectType({
  name: 'CVEReference',
  fields: () => ({
    href: {
      description: 'HREF for CVE reference',
      type: GraphQLString
    }
  })
})

const libraryClassUsageCount = new GraphQLObjectType({
  name: 'LibraryClassUsageCount',
  fields: () => ({
    appId: {
      description: 'ID for the application that the usage count is for',
      type: GraphQLString
    },
    usageCount: {
      description:
        'The number of classes used in the application for the library',
      type: GraphQLInt
    }
  })
})

export const libraryVulnerability = new GraphQLObjectType({
  name: 'LibraryVulnerability',
  fields: () => ({
    name: {
      description: 'CVE name for the vulnerability',
      type: new GraphQLNonNull(GraphQLString)
    },
    description: {
      description: 'Description for the vulnerability',
      type: new GraphQLNonNull(GraphQLString)
    },
    authentication: {
      description: 'Authentication for the vulnerability',
      type: GraphQLString
    },
    references: {
      description: 'References for the vulnerability',
      type: new GraphQLList(vulnerabilityReference)
    },
    visible: {
      description: 'Whether or not the vulnerability is visible',
      type: GraphQLBoolean
    },
    accessVector: {
      description: 'Access vector for the vulnerability',
      type: GraphQLString
    },
    accessComplexity: {
      description: 'Access complexity for the vulnerability',
      type: GraphQLString
    },
    availabilityImpact: {
      description: 'Availability impact for the vulnerability',
      type: GraphQLString
    },
    confidentialityImpact: {
      description: 'Confidentiality impact for the vulnerability',
      type: GraphQLString
    },
    integrityImpact: {
      description: 'Integrity impact for the vulnerability',
      type: GraphQLString
    },
    severityValue: {
      description: 'Severity value for the vulnerability',
      type: GraphQLString
    },
    severityCode: {
      description: 'Severity code for the vulnerability',
      type: GraphQLString
    },
    hasCve: {
      description: 'hasCve',
      type: GraphQLBoolean
    },
    hasCvss3: {
      description: 'hasCvss3',
      type: GraphQLBoolean
    },
    cvss3Vector: {
      description: 'Cvss3 Vector for the vulnerability',
      type: GraphQLString
    },
    cvss3SeverityValue: {
      description: 'Cvss3 Severity value for the vulnerability',
      type: GraphQLString
    },
    cvss3SeverityCode: {
      description: 'Severity code for the vulnerability',
      type: GraphQLString
    }
  })
})

export const libraryType = new GraphQLObjectType({
  name: 'Library',
  fields: () => ({
    id: {
      description:
        'The concatenation of the Library hash and language (e.g. hash|language)',
      type: new GraphQLNonNull(GraphQLID)
    },
    hash: {
      description:
        'The sha1 hash of the Library. Although not unique by itself, uniqueness is guaranteed for a specific hash/language combination.',
      type: new GraphQLNonNull(GraphQLString)
    },
    language: {
      description:
        'The language or framework the Library is made for (e.g. Java, Ruby)',
      type: new GraphQLNonNull(GraphQLString)
    },
    name: {
      description: 'The file name of the Library',
      type: new GraphQLNonNull(GraphQLString)
    },
    group: {
      description: 'The library group',
      type: GraphQLString
    },
    grade: {
      description: 'The grade of the Library',
      type: new GraphQLNonNull(gradeType)
    },
    usage: {
      description: 'The total usage of the library classes or files',
      type: new GraphQLNonNull(libraryUsageType)
    },
    policies: {
      description: '',
      type: new GraphQLNonNull(libraryPolicies)
    },
    versions: {
      description: 'Library versions info',
      type: new GraphQLNonNull(libraryVersionsInfo)
    },
    licenses: {
      description: 'Library licenses',
      type: new GraphQLNonNull(new GraphQLList(GraphQLString))
    },
    ossEnabled: {
      description: 'Is OSS enabled?',
      type: new GraphQLNonNull(GraphQLBoolean)
    },
    tags: {
      description: 'List of tags',
      type: new GraphQLNonNull(new GraphQLList(GraphQLString))
    },
    bugTrackers: {
      description: 'Bug Trackers',
      type: new GraphQLNonNull(new GraphQLList(bugTrackerType))
    },
    applications: {
      description: 'Applications related to this library',
      type: new GraphQLNonNull(new GraphQLList(libraryApplication))
    },
    mergedApplications: {
      description: 'Merged Applications related to this library',
      type: new GraphQLList(libraryMergedApplication)
    },
    vulnerabilities: {
      description: 'Vulnerabilities related to this library',
      type: new GraphQLList(libraryVulnerability)
    },
    libraryClassUsageCounts: {
      description:
        'A list of class usage counts for each application the library is associated with',
      type: new GraphQLList(libraryClassUsageCount)
    }
  })
})
